import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import EventCard from "./EventCard"; // Import the EventCard component
import useCRUD from "../../hooks/useCRUD"; // Adjust the import path as necessary
import ConfirmModal from "../Elements/ConfirmModal";
import Pagination from "../Elements/Pagination";

const EVENTS_PER_PAGE = 6;

const EventsPage = () => {
  const [activeTab, setActiveTab] = useState("future");
  const [futurePage, setFuturePage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const history = useHistory();

  const {
    data: events = [], // Provide an empty array as a default value
    isLoading,
    error,
    fetchItems: getEvents,
    deleteItem,
  } = useCRUD("/api/events");

  const handleDeleteEvent = async (id) => {
    setShowConfirmModal(false);
    try {
      await deleteItem(id);
      getEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  const handleManageClick = (id) => {
    // Navigate to the manage event page
    history.push(`/event/manage/${id}`);
  };

  const promptDeleteConfirmation = (id) => {
    setEventToDelete(id);
    setShowConfirmModal(true);
  };

  useEffect(() => {
    console.log("Fetching events...");
    getEvents().catch((err) => {
      console.log("Error fetching events:", err);
    });
  }, [getEvents]);

  // Filter events into future and past based on their dateOfOccurrence
  const futureEvents = events
    ?.filter((event) => new Date(event.dateOfOccurrence) >= new Date())
    .sort(
      (a, b) => new Date(a.dateOfOccurrence) - new Date(b.dateOfOccurrence),
    );
  // Sort past events in descending order
  const pastEvents = events
    ?.filter((event) => new Date(event.dateOfOccurrence) < new Date())
    .sort(
      (a, b) => new Date(b.dateOfOccurrence) - new Date(a.dateOfOccurrence),
    );

  const activeEvents = activeTab === "future" ? futureEvents : pastEvents;
  const activePage = activeTab === "future" ? futurePage : pastPage;
  const pagedEvents = activeEvents?.slice(
    (activePage - 1) * EVENTS_PER_PAGE,
    activePage * EVENTS_PER_PAGE,
  );

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "future") setFuturePage(1);
    if (tab === "past") setPastPage(1);
  };

  const handlePageChange = (page) => {
    const totalPages = Math.max(
      1,
      Math.ceil((activeEvents?.length || 0) / EVENTS_PER_PAGE),
    );
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    if (activeTab === "future") setFuturePage(nextPage);
    if (activeTab === "past") setPastPage(nextPage);
  };

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-6">
      {showConfirmModal && (
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={() => handleDeleteEvent(eventToDelete)}
          message="Are you sure you want to delete this event?"
        />
      )}
      <div className="mx-auto max-w-7xl">
        <div className="mb-5 rounded-3xl bg-white p-5 shadow-lg sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#004a7e]">
              Event management
            </p>
            <h1 className="mt-2 text-2xl font-black text-gray-900 sm:text-3xl">
              Events
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Review upcoming events, revisit past events, and jump into event
              setup.
            </p>
          </div>
          <Link
            to="/event/create"
            className="mt-4 inline-flex w-full items-center justify-center rounded-2xl bg-[#004a7e] px-4 py-3 text-sm font-bold text-white shadow-md hover:bg-[#003a64] sm:mt-0 sm:w-auto"
          >
            Create Event
          </Link>
        </div>
        <div className="tabs mb-4 flex overflow-hidden rounded-2xl bg-white p-1 shadow-sm">
          <button
            onClick={() => handleTabChange("future")}
            className={`flex-1 rounded-xl px-4 py-2 text-sm font-bold transition-colors ${
              activeTab === "future"
                ? "bg-[#004a7e] text-white"
                : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            }`}
          >
            Upcoming ({futureEvents?.length || 0})
          </button>
          <button
            onClick={() => handleTabChange("past")}
            className={`flex-1 rounded-xl px-4 py-2 text-sm font-bold transition-colors ${
              activeTab === "past"
                ? "bg-[#004a7e] text-white"
                : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            }`}
          >
            Past ({pastEvents?.length || 0})
          </button>
        </div>

        {isLoading ? (
          <p className="rounded-2xl bg-white p-5 text-gray-500 shadow-sm">
            Loading events...
          </p>
        ) : error ? (
          <p className="rounded-2xl bg-red-50 p-5 text-red-700 shadow-sm">
            Error Fetching Events
          </p>
        ) : activeEvents?.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {pagedEvents.map((event) => (
                <EventCard
                  key={event.id}
                  {...event}
                  onManageClick={handleManageClick}
                  onDeleteClick={() => promptDeleteConfirmation(event.id)}
                />
              ))}
            </div>
            <Pagination
              currentPage={activePage}
              totalItems={activeEvents.length}
              itemsPerPage={EVENTS_PER_PAGE}
              onPageChange={handlePageChange}
              label="events"
            />
          </>
        ) : (
          <div className="rounded-2xl bg-white p-8 text-center text-gray-500 shadow-sm">
            No {activeTab === "future" ? "upcoming" : "past"} events to display.
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
